import { paletteByKey, palettes } from '../marketing/marketingOptions'
import { fillTokens } from '../marketing/marketingRules'
import { euro } from '../../core/format/currency'
import { escapeHtml } from '../../core/format/html'
import { isSoldOut } from '../../core/rules/stock'
import { brandColors as c } from '../../data/brandColors'
import type { MarketingDesign } from '../../core/types/marketing'
import type { NewsletterBlock, NewsletterIssue } from '../../core/types/newsletter'
import type { Product } from '../../core/types/catalog'
import type { Article } from '../../core/types/content'
import type { MailBrand } from '../../core/types/mail'

/**
 * De nieuwsbrief als e-mail-HTML: één bestand dat Sarah in Brevo plakt ("Plak je eigen code").
 * Bewust ouderwets: tabellen, inline stijlen en systeemletters, want Outlook en Gmail lezen geen
 * moderne CSS of eigen lettertypen. 600 px breed, op de telefoon schaalt het mee.
 *
 * Geen meetpixels of UTM-tags: de privacytekst belooft "geen tracking". Brevo vult zelf de
 * afmeldlink in (`{{ unsubscribe }}`) en de link naar de webversie (`{{ mirror }}`).
 */

export type NewsletterContext = {
  tokens: Record<string, string>
  siteUrl: string
  salonName: string
  /** "Hoofdstraat 1, 1234 AB Plaats" */
  salonAddress: string
  design: (id: string) => MarketingDesign | undefined
  product: (id: string) => Product | undefined
  article: (slug: string) => Article | undefined
  /** Huisstijl & afzender (Mail & nieuwsbrief). Leeg = de salonnaam en het adres uit de instellingen. */
  brand?: MailBrand
}

/** Wat het frame van elke mail nodig heeft: nieuwsbrief én automatische mails. */
export type MailFrameContext = Pick<NewsletterContext, 'siteUrl' | 'salonName' | 'salonAddress' | 'brand'>

/** Het palet van de huisstijl. Lichte paletten krijgen een inktkleurige knop, donkere hun eigen kleur. */
export function mailColors(brand?: MailBrand) {
  const p = palettes.find((x) => x.key === brand?.palette) ?? palettes.find((x) => x.key === 'zand') ?? palettes[0]
  const dark = p.fg.toLowerCase() === c.shell.toLowerCase()
  return { band: p.bg, bandText: p.fg, button: dark ? p.bg : c.ink, buttonText: c.shell, link: dark ? p.bg : c.clay }
}

export const UNSUBSCRIBE_TAG = '{{ unsubscribe }}'
export const MIRROR_TAG = '{{ mirror }}'

const SERIF = "Georgia, 'Times New Roman', serif"
const SANS = "-apple-system, 'Segoe UI', Helvetica, Arial, sans-serif"

/** Eén definitie in core (ook voor facturen en pakbonnen). */
export { escapeHtml }

/** Een beeld moet een webadres zijn; uploads die alleen in deze browser staan (blob:, data:) kan een mail niet tonen. */
export function emailImage(src: string | undefined, siteUrl: string): string | null {
  const s = src?.trim()
  if (!s || s.startsWith('blob:') || s.startsWith('data:')) return null
  if (/^https?:\/\//i.test(s)) return s
  if (s.startsWith('//')) return `https:${s}`
  if (s.startsWith('/')) return `${siteUrl}${s}`
  return null
}

/** Alleen echte links in een knop; al het andere wordt een dode link (en een rode controle). */
export const safeUrl = (url: string) => (/^(https?:\/\/|mailto:)/i.test(url.trim()) ? url.trim() : null)

/** Tekst met witregels → alinea's, enkele enters → <br>. */
export function paragraphs(text: string, color: string, size = 16) {
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map(
      (p) =>
        `<p style="margin:0 0 14px;font-family:${SANS};font-size:${size}px;line-height:1.6;color:${color};">${escapeHtml(p).replace(/\n/g, '<br>')}</p>`,
    )
    .join('')
}

export function button(label: string, url: string, bg: string = c.ink, fg: string = c.shell) {
  const href = safeUrl(url) ?? '#'
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 0;"><tr><td style="border-radius:999px;background:${bg};"><a href="${escapeHtml(href)}" style="display:inline-block;padding:13px 26px;font-family:${SANS};font-size:15px;color:${fg};text-decoration:none;border-radius:999px;">${escapeHtml(label)}</a></td></tr></table>`
}

export const row = (inner: string, padding = '28px 36px', bg = '#ffffff') =>
  `<tr><td style="padding:${padding};background:${bg};">${inner}</td></tr>`

export const heading = (text: string, color: string = c.ink) =>
  `<h2 style="margin:0 0 12px;font-family:${SERIF};font-weight:normal;font-size:24px;line-height:1.25;color:${color};">${escapeHtml(text)}</h2>`

function designBlock(d: MarketingDesign, ctx: NewsletterContext) {
  const p = paletteByKey(d.palette)
  const t = (s: string) => fillTokens(s, ctx.tokens)
  const img = emailImage(d.image, ctx.siteUrl)
  const parts = [
    img ? `<img src="${escapeHtml(img)}" width="528" alt="" style="display:block;width:100%;max-width:528px;height:auto;border:0;margin:0 0 22px;border-radius:4px;">` : '',
    d.eyebrow ? `<p style="margin:0 0 10px;font-family:${SANS};font-size:12px;letter-spacing:2px;text-transform:uppercase;color:${p.accent};">${escapeHtml(t(d.eyebrow))}</p>` : '',
    d.headline || d.accent
      ? `<h1 style="margin:0 0 14px;font-family:${SERIF};font-weight:normal;font-size:32px;line-height:1.15;color:${p.fg};">${escapeHtml(t(d.headline))}${d.accent ? ` <em style="color:${p.accent};">${escapeHtml(t(d.accent))}</em>` : ''}</h1>`
      : '',
    d.body ? paragraphs(t(d.body), p.fg) : '',
    d.cta && d.qrUrl ? button(t(d.cta), d.qrUrl, p.fg, p.bg) : '',
  ]
  return row(parts.join(''), '36px', p.bg)
}

function productCell(p: Product, ctx: NewsletterContext) {
  const img = emailImage(p.image, ctx.siteUrl)
  const href = `${ctx.siteUrl}/webshop/${encodeURIComponent(p.id)}`
  return `<td valign="top" width="50%" style="padding:8px;">
<a href="${escapeHtml(href)}" style="text-decoration:none;color:${c.ink};">
${img ? `<img src="${escapeHtml(img)}" width="248" alt="${escapeHtml(p.imageAlt || `${p.brand} ${p.name}`)}" style="display:block;width:100%;max-width:248px;height:auto;border:0;background:${c.sand};border-radius:4px;">` : ''}
<p style="margin:10px 0 2px;font-family:${SANS};font-size:12px;color:${c.muted};">${escapeHtml(p.brand)}</p>
<p style="margin:0 0 4px;font-family:${SERIF};font-size:17px;line-height:1.3;color:${c.ink};">${escapeHtml(p.name)}</p>
<p style="margin:0;font-family:${SANS};font-size:14px;color:${c.ink};">${isSoldOut(p) ? 'Tijdelijk uitverkocht' : escapeHtml(euro(p.price))}</p>
</a></td>`
}

function productsBlock(b: Extract<NewsletterBlock, { kind: 'products' }>, ctx: NewsletterContext) {
  const items = b.productIds.map(ctx.product).filter((p): p is Product => Boolean(p))
  if (!items.length) return ''
  const rows: string[] = []
  for (let i = 0; i < items.length; i += 2) {
    const pair = items.slice(i, i + 2).map((p) => productCell(p, ctx))
    if (pair.length === 1) pair.push('<td width="50%" style="padding:8px;"></td>')
    rows.push(`<tr>${pair.join('')}</tr>`)
  }
  const grid = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 -8px;">${rows.join('')}</table>`
  return row(`${b.heading ? heading(fillTokens(b.heading, ctx.tokens)) : ''}${grid}`)
}

function articleBlock(a: Article, ctx: NewsletterContext) {
  const img = emailImage(a.image, ctx.siteUrl)
  const href = `${ctx.siteUrl}/journal/${encodeURIComponent(a.slug)}`
  return row(
    [
      img ? `<a href="${escapeHtml(href)}"><img src="${escapeHtml(img)}" width="528" alt="" style="display:block;width:100%;max-width:528px;height:auto;border:0;margin:0 0 18px;border-radius:4px;"></a>` : '',
      `<p style="margin:0 0 8px;font-family:${SANS};font-size:12px;color:${c.clay};">Uit het journal · ${escapeHtml(a.readTime)}</p>`,
      heading(a.title),
      paragraphs(a.excerpt, c.muted, 15),
      `<a href="${escapeHtml(href)}" style="font-family:${SANS};font-size:15px;color:${c.clay};">Lees verder</a>`,
    ].join(''),
  )
}

function blockHtml(b: NewsletterBlock, ctx: NewsletterContext): string {
  const t = (s: string) => fillTokens(s, ctx.tokens)
  switch (b.kind) {
    case 'design': {
      const d = ctx.design(b.designId)
      return d ? designBlock(d, ctx) : ''
    }
    case 'text':
      if (!b.heading.trim() && !b.body.trim()) return ''
      return row(`${b.heading.trim() ? heading(t(b.heading)) : ''}${paragraphs(t(b.body), c.ink)}`)
    case 'products':
      return productsBlock(b, ctx)
    case 'article': {
      const a = ctx.article(b.slug)
      return a ? articleBlock(a, ctx) : ''
    }
    case 'button': {
      const k = mailColors(ctx.brand)
      return b.label.trim() ? row(button(t(b.label), b.url, k.button, k.buttonText), '8px 36px 28px') : ''
    }
  }
}

type Frame = {
  subject: string
  preheader: string
  body: string
  /** Waarom krijgt de lezer dit: bij de nieuwsbrief met afmeldlink, bij een automatische mail niet. */
  kind: 'newsletter' | 'transactional'
}

const socialLink = (label: string, url: string) =>
  safeUrl(url) ? ` · <a href="${escapeHtml(url)}" style="color:${c.muted};">${label}</a>` : ''

/**
 * Het vaste frame van elke mail: logo of naam bovenaan, de vaste voettekst onderaan. Gedeeld door
 * de nieuwsbrief en de voorbeelden van de automatische mails, zodat ze er hetzelfde uitzien.
 * De afmeldlink is verplicht in de nieuwsbrief en staat er daarom altijd in, ongeacht de huisstijl.
 */
export function mailFrame({ subject, preheader, body, kind }: Frame, ctx: MailFrameContext): string {
  const brand = ctx.brand
  const k = mailColors(brand)
  const logo = emailImage(brand?.logo, ctx.siteUrl)
  const name = escapeHtml(ctx.salonName)
  const header = logo
    ? `<img src="${escapeHtml(logo)}" alt="${name}" height="44" style="display:inline-block;height:44px;width:auto;border:0;">`
    : name
  const address = escapeHtml(brand?.footer.address.trim() || `${ctx.salonName} · ${ctx.salonAddress}`)
  const note = brand?.footer.note.trim()
  const socials = brand ? `${socialLink('Instagram', brand.footer.instagram)}${socialLink('Facebook', brand.footer.facebook)}` : ''
  const reason =
    kind === 'newsletter'
      ? `Je krijgt deze mail omdat je je hebt aangemeld voor de nieuwsbrief. <a href="${UNSUBSCRIBE_TAG}" style="color:${c.muted};">Afmelden</a> · <a href="${MIRROR_TAG}" style="color:${c.muted};">Bekijk in je browser</a>`
      : 'Je krijgt deze mail omdat je iets bij ons bestelde, boekte of een account hebt.'
  const small = `margin:0 0 6px;font-family:${SANS};font-size:13px;line-height:1.6;color:${c.muted};`
  const footer = row(
    `${note ? `<p style="${small}">${escapeHtml(note)}</p>` : ''}<p style="${small}">${address}${socials}</p>
<p style="${small}margin:0;">${reason}</p>`,
    '28px 36px 36px',
    c.sand,
  )

  return `<!doctype html>
<html lang="nl">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="x-apple-disable-message-reformatting">
<title>${escapeHtml(subject)}</title>
</head>
<body style="margin:0;padding:0;background:${c.sand};">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;">${escapeHtml(preheader)}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${c.sand};">
<tr><td align="center" style="padding:24px 12px;">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:600px;">
<tr><td style="padding:18px 36px;background:${k.band};font-family:${SERIF};font-size:22px;color:${k.bandText};text-align:center;">${header}</td></tr>
${body}
${footer}
</table>
</td></tr>
</table>
</body>
</html>
`
}

/** De volledige mail, klaar om in Brevo te plakken. */
export function newsletterHtml(issue: NewsletterIssue, ctx: NewsletterContext): string {
  const t = (s: string) => fillTokens(s, ctx.tokens)
  const body = issue.blocks.map((b) => blockHtml(b, ctx)).join('\n')
  return mailFrame({ subject: t(issue.subject), preheader: t(issue.preheader), body, kind: 'newsletter' }, ctx)
}
